/** `rule.resource` — a named meter that drains, refills and runs out: a shift
 * clock, fuel, a bomb bay. Integer-tick friendly: capacity and drain are in
 * units (a clock's unit is one tick). Keyed per instance (`key: 'shift'`), so
 * one game can carry several.
 *  - drains `drainPerTick` × the optional drain scalar while running;
 *  - `fillOn` signals refill it to capacity (× the optional capacity scalar)
 *    and (re)start it; `adjust` rules add or spend on a tagged signal
 *    (a collected time bonus, a dropped bomb);
 *  - `cashOutOn` signals score what is left (per seat) and park it until the
 *    next fill — "remaining time converts to bonus";
 *  - empty: emits `depleted` once, then `damage`s every victim-tagged entity
 *    and refills (onEmpty `damage`), loses the match (`lose` — a shift or
 *    match clock), or just stays empty (`none`). */
import { bool, id, list, num, obj, oneOf, readsSignal } from '../schemaBuilders.js';
import { defineModule, num as read, type ModuleCtx } from '../module.js';
import { COMPOSE_TICK_HZ } from '../world.js';

/** A seconds meter's level as its HUD reads it: whole seconds, rounded up. */
export const resourceSecondsV1 = (level: number): number => Math.ceil(level / COMPOSE_TICK_HZ);

export type ResourceConfig = {
  label: string;
  display: 'seconds' | 'units' | 'percent';
  capacity: number;
  capacityScalar?: string;
  drainPerTick: number;
  drainScalar?: string;
  startRunning: boolean;
  fillOn: string[];
  adjust: Array<{ signal: string; fromTag: string; amount: number }>;
  cashOutOn: string[];
  /** Points per unit left at cash-out. */
  cashOutPoints: number;
  onEmpty: 'damage' | 'lose' | 'none';
  /** Who an emptying `damage` meter hurts (required for `damage` only). */
  victimTag?: string;
  /** Named scalar mirroring the level, for a module that spends from the
   * meter (a bomb bay refusing to drop when empty). */
  scalar?: string;
};

type Ctx = ModuleCtx<ResourceConfig>;

function capacityOf(ctx: Ctx): number {
  const c = ctx.config;
  return c.capacity * (c.capacityScalar ? ctx.scalar(c.capacityScalar, 1) : 1);
}

function fill(ctx: Ctx): void {
  Object.assign(ctx.state, { level: capacityOf(ctx), running: true, empty: false });
}

function mirror(ctx: Ctx): void {
  if (ctx.config.scalar !== undefined) ctx.setScalar(ctx.config.scalar, read(ctx.state, 'level'));
}

export const resource = defineModule<ResourceConfig>({
  id: 'rule.resource', version: 1, attach: 'global', role: 'rule',
  summary: 'A named meter (clock, fuel, ammo): drains per tick, refills/adjusts on signals, cashes out to score, and on empty damages, loses the match or stays empty.',
  config: obj({
    label: id(), display: oneOf('seconds', 'units', 'percent'), capacity: num(1, 1e6), capacityScalar: id(),
    drainPerTick: num(0, 1000), drainScalar: id(), startRunning: bool(), fillOn: list(readsSignal(), 4),
    adjust: list(obj({ signal: readsSignal(), fromTag: id(), amount: num(-1e6, 1e6) }), 6),
    cashOutOn: list(readsSignal(), 4), cashOutPoints: num(0, 1000), onEmpty: oneOf('damage', 'lose', 'none'), victimTag: id(), scalar: id(),
  }, ['capacityScalar', 'drainScalar', 'victimTag', 'scalar']),
  requires: [], events: ['depleted', 'refill', 'cash-out'],
  presentation: { depleted: { cue: 'timeup', vfx: { effect: 'shake' } }, refill: { cue: 'powerup' },
    'cash-out': { cue: 'score' } },
  proofs: ['drains-per-tick-scaled', 'fill-on-signal-scaled-capacity', 'adjust-on-tagged-signal-clamped', 'cash-out-scores-and-parks',
    'empty-damages-and-refills', 'empty-loses-the-match', 'empty-stays-empty', 'hud-display', 'mirrors-level-scalar'],
  animStates: () => [],
  check: c => [
    ...(c.onEmpty !== 'none' && c.drainPerTick === 0 && c.adjust.every(a => a.amount >= 0) ? [`a meter that ${c.onEmpty === 'lose' ? 'loses' : 'damages'} on empty must be able to run out`] : []),
    ...(c.onEmpty === 'damage' && c.victimTag === undefined ? ['a damaging meter needs victimTag'] : []),
  ],
  hud: c => [{ id: 'level', label: c.label, kind: c.display === 'seconds' ? 'timer' : c.display === 'percent' ? 'meter' : 'counter' }],
  hudValues(ctx) {
    const level = read(ctx.state, 'level');
    const c = ctx.config;
    const value = c.display === 'seconds' ? resourceSecondsV1(level) : c.display === 'percent' ? Math.round((100 * level) / capacityOf(ctx)) : Math.round(level);
    return { level: value };
  },
  initGlobal(ctx) {
    Object.assign(ctx.state, { level: 0, running: false, empty: false });
    if (ctx.config.startRunning) fill(ctx);
    mirror(ctx);
  },
  systems: {
    // Rules (globals run first): drain and run out before vitals reads damage.
    rules(ctx) {
      const c = ctx.config, s = ctx.state;
      if (s.running !== true) return;
      const drain = c.drainPerTick * (c.drainScalar ? ctx.scalar(c.drainScalar, 1) : 1);
      s.level = Math.max(0, read(s, 'level') - drain);
      mirror(ctx);
      if (read(s, 'level') > 0 || s.empty === true) return;
      s.empty = true;
      ctx.emit('depleted', { meter: ctx.key });
      if (c.onEmpty === 'lose') { ctx.end('lost', 'depleted'); return; }
      if (c.onEmpty !== 'damage') return;
      for (const v of ctx.entities({ tag: c.victimTag!, status: 'active' })) ctx.signal('damage', v, null, { amount: 1, x: v.x, y: v.y - v.h / 2, tags: [...v.tags] });
      fill(ctx);
    },
    // Flow: after the rules and the director that raise fill/adjust/cash-out signals.
    flow(ctx) {
      const c = ctx.config, s = ctx.state;
      if (s.running === true && c.cashOutOn.some(kind => ctx.signals(kind).length)) {
        const points = Math.floor(read(s, 'level') * c.cashOutPoints);
        for (let slot = 0; slot < ctx.world.playerCount; slot++) ctx.addScore(slot, points);
        ctx.emit('cash-out', { meter: ctx.key, points });
        Object.assign(s, { level: 0, running: false });
      }
      if (c.fillOn.some(kind => ctx.signals(kind).length)) fill(ctx);
      for (const rule of c.adjust) {
        for (const sig of ctx.signals(rule.signal)) {
          const tags = sig.data.tags;
          if (!Array.isArray(tags) || !tags.includes(rule.fromTag)) continue;
          s.level = Math.min(capacityOf(ctx), Math.max(0, read(s, 'level') + rule.amount));
          if (rule.amount > 0) { s.empty = false; ctx.emit('refill', { meter: ctx.key, amount: rule.amount }); }
        }
      }
      mirror(ctx);
    },
  },
});
